import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import {
  useUploadFilesMutation,
  FileNameTypes,
  useUpdateFileStatusMutation,
  FileUploadStatus,
  GetAssemblyAttachmentsDocument,
  GetAssemblyDetailsDocument,
} from '@/__generated__/graphql';
import { canvasToImage } from '@/utils/dom';
import { DEFAULT_SCREENSHOT_DARK_BACKGROUND } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useCaptureScreenshot } from '@/hooks/useCaptureScreenshot';

export const useSaveAssemblyAttachment = (
  referenceId: string,
  collectionName: string,
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>,
  isComponentOverview = false
) => {
  const [uploadFiles] = useUploadFilesMutation();
  const [updateFileStatus] = useUpdateFileStatusMutation();
  const structureId = useGetStructureId();
  const callbacks = useAtomValue(state.globalCallbacks);
  const { showSnackBar } = useSnackBarMessage({});

  const { captureElement } = useCaptureScreenshot();

  const uploadAttachments = useCallback(
    async (attchments: Attachment[]) => {
      const { data } = await uploadFiles({
        variables: {
          input: {
            structureId,
            referenceId,
            fileNameType: FileNameTypes.UserAttachments,
            collectionName,
            files: attchments
              .filter(({ file, uuid }) => file && uuid)
              .map(({ file, name, type, uuid }) => ({
                uuid: uuid!,
                name,
                mimeType: type,
                size: file?.size!,
              })),
          },
        },
      });

      const presignedAttachments = data?.preSignedUrlToUploadFiles ?? [];
      if (presignedAttachments.length === 0) return [];

      for (const { url, id, uuid } of presignedAttachments) {
        const attachment = attchments.find((att) => att.uuid === uuid);
        if (!attachment || !attachment.file) continue;
        const uploadResponse = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': attachment.type },
          body: attachment.file,
        });

        if (uploadResponse.status === 200) {
          await updateFileStatus({
            variables: {
              input: {
                referenceId,
                collectionName,
                fileUpdates: [{ fileId: id, uploadStatus: FileUploadStatus.Success }],
              },
            },
            refetchQueries: [GetAssemblyAttachmentsDocument, GetAssemblyDetailsDocument],
          });
        }
      }
      return presignedAttachments;
    },
    [uploadFiles, structureId, referenceId, collectionName, isComponentOverview, updateFileStatus]
  );

  const handleCaptureScreenshot = useCallback(async () => {
    if (setIsUpdating) setIsUpdating(true);
    try {
      // Use the same approach as getViewpointScreenshot to find the element to capture
      const container = document.getElementById('outer-viewer');
      let imageBase64;

      if (!container) {
        const abyssViewerParent = document.querySelector('canvas');

        if (!abyssViewerParent) {
          const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();
          imageBase64 = canvasToImage(htmlCanvas, {
            fillStyle: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
          });
        } else {
          imageBase64 = await captureElement(abyssViewerParent);
        }
      } else {
        imageBase64 = await captureElement(container);
      }

      if (!imageBase64) {
        showSnackBar('Failed to capture screenshot', 'error');
        return;
      }

      const fileName = `assembly-screenshot-${Date.now()}.png`;
      const res = await fetch(imageBase64);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: 'image/png' });
      await uploadAttachments([
        {
          file,
          name: fileName,
          type: 'image/png',
          caption: '',
          isNew: true,
          uuid: `file-${Date.now()}`,
        },
      ]);
    } finally {
      if (setIsUpdating) setIsUpdating(false);
    }
  }, [callbacks, captureElement, setIsUpdating, showSnackBar, uploadAttachments]);

  return { handleCaptureScreenshot, uploadAttachments };
};
